import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
  Paper,
} from '@mui/material';
import { ExpandMore, CheckCircle, BarChart } from '@mui/icons-material';
import {
  actualDataService,
  CreditTypeSummary,
} from '../../services/actualDataService';

interface ActualDataSelectorProps {
  dataSourceId: number;
  onSelectionChange: (yearMonth: string, creditTypeIds: number[], totalCases: number, creditTypeNames: string) => void;
  disabled?: boolean;
}

interface YearData {
  year: number;
  totalCases: number;
  months?: Array<{ month: number; totalCases: number }>;
  loadingMonths?: boolean;
}

interface MonthData {
  month: number;
  totalCases: number;
  creditTypes?: CreditTypeSummary[];
  loadingCreditTypes?: boolean;
}

export const ActualDataSelector: React.FC<ActualDataSelectorProps> = ({
  dataSourceId,
  onSelectionChange,
  disabled = false,
}) => {
  const [years, setYears] = useState<YearData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedCreditTypes, setSelectedCreditTypes] = useState<Set<number>>(new Set());

  // Load years on mount (FAST!)
  useEffect(() => {
    loadYears();
  }, [dataSourceId]);

  useEffect(() => {
    if (selectedYear && selectedMonth && selectedCreditTypes.size > 0) {
      const yearMonth = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      const creditTypeIds = Array.from(selectedCreditTypes);

      // Calculate total cases for selected credit types
      const yearData = years.find(y => y.year === selectedYear);
      const monthData = yearData?.months?.find(m => m.month === selectedMonth);

      let totalCases = 0;
      const creditTypeNamesArray: string[] = [];
      if (monthData) {
        const creditTypesData = (monthData as MonthData).creditTypes;
        if (creditTypesData) {
          creditTypesData.forEach(ct => {
            if (selectedCreditTypes.has(ct.creditTypeId)) {
              totalCases += ct.totalCases;
              creditTypeNamesArray.push(ct.creditTypeName);
            }
          });
        }
      }

      const creditTypeNames = creditTypeNamesArray.join(', ');
      onSelectionChange(yearMonth, creditTypeIds, totalCases, creditTypeNames);
    }
  }, [selectedYear, selectedMonth, selectedCreditTypes]);

  const loadYears = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading years (fast query)...');
      const yearsData = await actualDataService.getYears(dataSourceId);
      setYears(yearsData.map(y => ({ ...y, months: undefined, loadingMonths: false })));
      console.log(`Loaded ${yearsData.length} years`);
    } catch (err: any) {
      console.error('Failed to load years:', err);
      setError(err.response?.data?.error || 'Nem sikerült betölteni az éveket');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthsForYear = async (year: number) => {
    try {
      console.log(`Loading months for year ${year} (lazy load)...`);

      // Set loading state
      setYears(prev => prev.map(y =>
        y.year === year ? { ...y, loadingMonths: true } : y
      ));

      const monthsData = await actualDataService.getMonths(dataSourceId, year);

      // Update state with months
      setYears(prev => prev.map(y =>
        y.year === year ? { ...y, months: monthsData, loadingMonths: false } : y
      ));

      console.log(`Loaded ${monthsData.length} months for year ${year}`);
    } catch (err: any) {
      console.error(`Failed to load months for year ${year}:`, err);
      setError(err.response?.data?.error || 'Nem sikerült betölteni a hónapokat');

      // Clear loading state
      setYears(prev => prev.map(y =>
        y.year === year ? { ...y, loadingMonths: false } : y
      ));
    }
  };

  const loadCreditTypesForMonth = async (year: number, month: number) => {
    try {
      console.log(`Loading credit types for ${year}-${month} (lazy load)...`);

      // Set loading state on the specific month
      setYears(prev => prev.map(y => {
        if (y.year === year && y.months) {
          return {
            ...y,
            months: y.months.map(m =>
              m.month === month
                ? { year, month: m.month, totalCases: m.totalCases, loadingCreditTypes: true } as MonthData
                : m
            ),
          };
        }
        return y;
      }));

      const creditTypesData = await actualDataService.getCreditTypesForMonth(dataSourceId, year, month);

      // Update state with credit types
      setYears(prev => prev.map(y => {
        if (y.year === year && y.months) {
          return {
            ...y,
            months: y.months.map(m =>
              m.month === month
                ? { year, month: m.month, totalCases: m.totalCases, creditTypes: creditTypesData, loadingCreditTypes: false } as MonthData
                : m
            ),
          };
        }
        return y;
      }));

      console.log(`Loaded ${creditTypesData.length} credit types for ${year}-${month}`);
    } catch (err: any) {
      console.error(`Failed to load credit types for ${year}-${month}:`, err);
      setError(err.response?.data?.error || 'Nem sikerült betölteni a hiteltípusokat');

      // Clear loading state
      setYears(prev => prev.map(y => {
        if (y.year === year && y.months) {
          return {
            ...y,
            months: y.months.map(m =>
              m.month === month
                ? { year, month: m.month, totalCases: m.totalCases, loadingCreditTypes: false } as MonthData
                : m
            ),
          };
        }
        return y;
      }));
    }
  };

  const handleYearClick = (year: number) => {
    if (selectedYear === year) {
      // Collapse
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedCreditTypes(new Set());
    } else {
      // Expand and load months if not loaded
      setSelectedYear(year);
      setSelectedMonth(null);
      setSelectedCreditTypes(new Set());

      const yearData = years.find(y => y.year === year);
      if (!yearData?.months && !yearData?.loadingMonths) {
        loadMonthsForYear(year);
      }
    }
  };

  const handleMonthClick = (year: number, month: number) => {
    if (selectedMonth === month) {
      // Collapse
      setSelectedMonth(null);
      setSelectedCreditTypes(new Set());
    } else {
      // Expand and load credit types if not loaded
      setSelectedMonth(month);
      setSelectedCreditTypes(new Set());

      const yearData = years.find(y => y.year === year);
      const monthData = yearData?.months?.find(m => m.month === month) as MonthData | undefined;

      if (monthData && !monthData.creditTypes && !monthData.loadingCreditTypes) {
        loadCreditTypesForMonth(year, month);
      }
    }
  };

  const handleCreditTypeToggle = (creditTypeId: number) => {
    const newSelection = new Set(selectedCreditTypes);
    if (newSelection.has(creditTypeId)) {
      newSelection.delete(creditTypeId);
    } else {
      newSelection.add(creditTypeId);
    }
    setSelectedCreditTypes(newSelection);
  };

  const formatMonth = (month: number): string => {
    const monthNames = [
      'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
      'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December',
    ];
    return monthNames[month - 1];
  };

  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    return num.toLocaleString('hu-HU');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }}>Évek betöltése...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadYears} sx={{ ml: 2 }} size="small">
          Újrapróbálás
        </Button>
      </Alert>
    );
  }

  if (years.length === 0) {
    return (
      <Alert severity="info">
        Nincs elérhető adat ebben az adatforrásban.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BarChart /> Adatkiválasztás
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Válassz évet, hónapot és hiteltípus(oka)t.
      </Typography>

      {/* Years */}
      {years.map((yearData) => (
        <Accordion
          key={yearData.year}
          expanded={selectedYear === yearData.year}
          onChange={() => handleYearClick(yearData.year)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              {selectedYear === yearData.year && (
                <CheckCircle color="primary" fontSize="small" />
              )}
              <Typography variant="h6" fontWeight={600}>
                {yearData.year}
              </Typography>
              <Chip
                label={`${formatNumber(yearData.totalCases)} ügy`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {yearData.loadingMonths && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Hónapok betöltése...
                </Typography>
              </Box>
            )}
            {yearData.months && yearData.months.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nincsenek hónapok erre az évre.
              </Typography>
            )}
            {/* Months */}
            {yearData.months?.map((monthData) => {
              const monthDataFull = monthData as MonthData;
              return (
                <Accordion
                  key={monthData.month}
                  expanded={selectedMonth === monthData.month && selectedYear === yearData.year}
                  onChange={() => handleMonthClick(yearData.year, monthData.month)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      {selectedMonth === monthData.month && (
                        <CheckCircle color="success" fontSize="small" />
                      )}
                      <Typography fontWeight={500}>
                        {formatMonth(monthData.month)}
                      </Typography>
                      <Chip
                        label={`${formatNumber(monthData.totalCases)} ügy`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {monthDataFull.loadingCreditTypes && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          Hiteltípusok betöltése...
                        </Typography>
                      </Box>
                    )}
                    {monthDataFull.creditTypes && monthDataFull.creditTypes.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Nincsenek hiteltípusok erre a hónapra.
                      </Typography>
                    )}
                    {monthDataFull.creditTypes && monthDataFull.creditTypes.length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Hiteltípusok kiválasztása:
                        </Typography>
                        {/* Credit Types */}
                        {monthDataFull.creditTypes.map((creditType) => (
                          <Paper
                            key={creditType.creditTypeId}
                            sx={{
                              p: 1.5,
                              mb: 1,
                              bgcolor: selectedCreditTypes.has(creditType.creditTypeId)
                                ? 'action.selected'
                                : 'background.paper',
                              cursor: 'pointer',
                              border: 1,
                              borderColor: selectedCreditTypes.has(creditType.creditTypeId)
                                ? 'primary.main'
                                : 'divider',
                            }}
                            onClick={() => handleCreditTypeToggle(creditType.creditTypeId)}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Checkbox
                                checked={selectedCreditTypes.has(creditType.creditTypeId)}
                                size="small"
                                sx={{ p: 0, mt: 0.5 }}
                                disabled={disabled}
                              />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {creditType.creditTypeName}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={`${formatNumber(creditType.totalCases)} ügy`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={`Vételár: ${formatNumber(Math.round(creditType.totalPurchasePrice || 0))} Ft`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={`Tartozás: ${formatNumber(Math.round(creditType.totalInitialDebt || 0))} Ft`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                        {selectedCreditTypes.size > 0 && (
                          <Alert severity="success" sx={{ mt: 2 }}>
                            <strong>{selectedCreditTypes.size} hiteltípus</strong> kiválasztva
                          </Alert>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
